import { useAuth } from '@context/AuthContext';
import { useSidebarContext } from '@context/SidebarContext';
import { useMySubscription } from '@hooks/api';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { NAV_GROUPS } from '@routes/navConfig';
import { cn } from '@utils/cn';

import SidebarNavItem from './SidebarNavItem';
import UserTile from './UserTile';

interface SidebarProps {
  senderBadge?: number;
}

export default function Sidebar({ senderBadge }: SidebarProps) {
  const { collapsed, mobileOpen, closeMobile } = useSidebarContext();
  const { user } = useAuth();
  const { data: subscription } = useMySubscription();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // The icon-rail is a desktop-only affordance; the mobile drawer always
  // renders full labels regardless of the persisted collapse preference.
  const railed = isDesktop && collapsed;

  const name = user ? user.name || user.email : '';
  const plan = subscription ? `${subscription.plan.name} plan` : 'Free plan';

  return (
    <nav
      aria-label="Primary navigation"
      className={cn(
        // Mobile: off-canvas drawer that slides in below the topbar.
        'fixed left-0 top-topbar z-40 h-[calc(100vh-var(--topbar-h))] w-sidebar-w',
        'flex flex-col overflow-y-auto overflow-x-hidden border-r border-line bg-surface py-3',
        'transition-transform duration-200 ease-out',
        mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
        // Desktop (lg+): in-flow sticky column, always visible, width by collapse.
        'lg:sticky lg:z-auto lg:translate-x-0 lg:shadow-none lg:shrink-0 lg:transition-[width]',
        railed ? 'lg:w-sidebar-min' : 'lg:w-sidebar-w',
      )}
    >
      {NAV_GROUPS.map((group, groupIndex) => (
        <div key={group.label} className="px-2.5">
          {groupIndex > 0 ? <div className="mx-3.5 my-2 h-px bg-line" /> : null}
          {!railed ? (
            <div className="px-3.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-t3">
              {group.label}
            </div>
          ) : null}
          {group.items.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              end={item.to === '/'}
              collapsed={railed}
              onNavigate={closeMobile}
              badge={
                item.badgeKey === 'senders' && senderBadge && senderBadge > 0 ? (
                  <span className="rounded-[10px] bg-accent-red px-2 py-[2px] text-[10px] font-bold text-white">
                    {senderBadge}
                  </span>
                ) : null
              }
            />
          ))}
        </div>
      ))}

      <div className="mt-auto border-t border-line p-2.5">
        <UserTile name={name} plan={plan} collapsed={railed} />
      </div>
    </nav>
  );
}
